import React, { useState, useEffect } from 'react';
import { projectAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, Trash2, GripVertical, User, Clock, Flag, Tag, 
  LayoutGrid, List, Columns, Search, Filter, X, Edit2, Check
} from 'lucide-react';
import { toast } from 'sonner';

const statusColumns = [
  { id: 'todo', label: 'To Do', color: 'bg-slate-500' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-blue-500' },
  { id: 'review', label: 'Review', color: 'bg-amber-500' },
  { id: 'done', label: 'Done', color: 'bg-emerald-500' },
];

const priorityColors = {
  critical: 'bg-red-500/10 text-red-400 border-red-500/20',
  high: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  low: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
};

export default function TasksPanel({ projectId, members = [], epics = [], onRefresh }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban'); // kanban, list, scrum
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [editingTask, setEditingTask] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);

  const [newTask, setNewTask] = useState({
    title: '', description: '', status: 'todo', priority: 'medium',
    task_type: 'task', assignee_id: '', sprint: '', labels: [], epic_id: ''
  });

  useEffect(() => {
    loadTasks();
  }, [projectId]);

  const loadTasks = async () => {
    try {
      const res = await projectAPI.listTasks(projectId);
      setTasks(res.data);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      await projectAPI.createTask(projectId, newTask);
      setNewTask({ title: '', description: '', status: 'todo', priority: 'medium', task_type: 'task', assignee_id: '', sprint: '', labels: [], epic_id: '' });
      setShowAddForm(false);
      loadTasks();
      toast.success('Task created');
    } catch (err) {
      toast.error('Failed to create task');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await projectAPI.updateTask(projectId, taskId, updates);
      loadTasks();
      toast.success('Task updated');
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await projectAPI.deleteTask(projectId, taskId);
      loadTasks();
      toast.success('Task deleted');
    } catch (err) {
      toast.error('Failed to delete task');
    }
  };

  const handleDragStart = (task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (newStatus) => {
    if (draggedTask && draggedTask.status !== newStatus) {
      await handleUpdateTask(draggedTask.id, { status: newStatus });
    }
    setDraggedTask(null);
  };

  const filteredTasks = tasks.filter(task => {
    if (searchTerm && !task.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterStatus && task.status !== filterStatus) return false;
    if (filterAssignee && task.assignee_id !== filterAssignee) return false;
    return true;
  });

  const getMemberName = (memberId) => {
    const member = members.find(m => m.user_id === memberId);
    return member?.name || member?.email || 'Unassigned';
  };

  const getEpicTitle = (epicId) => {
    const epic = epics.find(e => e.id === epicId);
    return epic?.title || '';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin h-6 w-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--surface-border)] flex items-center gap-2 flex-wrap">
        <Badge className="bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)] border-[var(--accent-indigo)]/20 text-xs">
          <LayoutGrid className="w-3.5 h-3.5 mr-1" /> Tasks
        </Badge>
        <div className="flex items-center gap-1 ml-2">
          <Button size="sm" variant={viewMode === 'kanban' ? 'secondary' : 'ghost'} className="h-8 text-sm px-3" onClick={() => setViewMode('kanban')} data-testid="view-kanban">
            <Columns className="w-4 h-4 mr-1" /> Kanban
          </Button>
          <Button size="sm" variant={viewMode === 'list' ? 'secondary' : 'ghost'} className="h-8 text-sm px-3" onClick={() => setViewMode('list')} data-testid="view-list">
            <List className="w-4 h-4 mr-1" /> List
          </Button>
        </div>
        <div className="flex-1" />
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-40" />
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="h-8 text-sm pl-8 w-40 bg-transparent border-[var(--surface-border)]" data-testid="task-search" />
        </div>
        <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-32 text-sm bg-transparent border-[var(--surface-border)]" data-testid="filter-status">
            <Filter className="w-4 h-4 mr-1" /><SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
            <SelectItem value="all" className="text-sm">All</SelectItem>
            {statusColumns.map(s => <SelectItem key={s.id} value={s.id} className="text-sm">{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" className="h-8 text-sm bg-[var(--accent-indigo)] hover:bg-[var(--accent-indigo)]/90 text-white" onClick={() => setShowAddForm(true)} data-testid="add-task-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Task
        </Button>
      </div>

      {/* Add Task Form */}
      {showAddForm && (
        <div className="p-4 border-b border-[var(--surface-border)] bg-[var(--surface-card)]">
          <div className="grid grid-cols-2 gap-4 max-w-2xl">
            <div className="col-span-2">
              <Label className="text-sm mb-1.5 block opacity-60">Title *</Label>
              <Input value={newTask.title} onChange={(e) => setNewTask(t => ({ ...t, title: e.target.value }))} placeholder="Task title" className="h-9 text-sm bg-transparent border-[var(--surface-border)]" data-testid="new-task-title" />
            </div>
            <div className="col-span-2">
              <Label className="text-sm mb-1.5 block opacity-60">Description</Label>
              <Textarea value={newTask.description} onChange={(e) => setNewTask(t => ({ ...t, description: e.target.value }))} placeholder="Description..." className="text-sm bg-transparent border-[var(--surface-border)] min-h-[70px]" data-testid="new-task-desc" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block opacity-60">Type</Label>
              <Select value={newTask.task_type} onValueChange={(v) => setNewTask(t => ({ ...t, task_type: v }))}>
                <SelectTrigger className="h-9 text-sm bg-transparent border-[var(--surface-border)]" data-testid="new-task-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                  <SelectItem value="task" className="text-sm">Task</SelectItem>
                  <SelectItem value="story" className="text-sm">Story</SelectItem>
                  <SelectItem value="bug" className="text-sm">Bug</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block opacity-60">Priority</Label>
              <Select value={newTask.priority} onValueChange={(v) => setNewTask(t => ({ ...t, priority: v }))}>
                <SelectTrigger className="h-9 text-sm bg-transparent border-[var(--surface-border)]" data-testid="new-task-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                  <SelectItem value="low" className="text-sm">Low</SelectItem>
                  <SelectItem value="medium" className="text-sm">Medium</SelectItem>
                  <SelectItem value="high" className="text-sm">High</SelectItem>
                  <SelectItem value="critical" className="text-sm">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block opacity-60">Assignee</Label>
              <Select value={newTask.assignee_id || 'unassigned'} onValueChange={(v) => setNewTask(t => ({ ...t, assignee_id: v === 'unassigned' ? '' : v }))}>
                <SelectTrigger className="h-9 text-sm bg-transparent border-[var(--surface-border)]" data-testid="new-task-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                  <SelectItem value="unassigned" className="text-sm">Unassigned</SelectItem>
                  {members.map(m => <SelectItem key={m.user_id} value={m.user_id} className="text-sm">{m.name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block opacity-60">Epic</Label>
              <Select value={newTask.epic_id || 'none'} onValueChange={(v) => setNewTask(t => ({ ...t, epic_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="h-9 text-sm bg-transparent border-[var(--surface-border)]" data-testid="new-task-epic"><SelectValue placeholder="No Epic" /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                  <SelectItem value="none" className="text-sm">No Epic</SelectItem>
                  {epics.map(e => <SelectItem key={e.id} value={e.id} className="text-sm">{e.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex gap-2">
              <Button size="sm" className="h-9 text-sm bg-[var(--accent-indigo)] hover:bg-[var(--accent-indigo)]/90 text-white" onClick={handleCreateTask} data-testid="create-task-btn">
                <Check className="w-4 h-4 mr-1" /> Create Task
              </Button>
              <Button size="sm" variant="ghost" className="h-9 text-sm" onClick={() => setShowAddForm(false)} data-testid="cancel-task-btn">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-4 p-4 min-w-max h-full">
            {statusColumns.map(column => {
              const columnTasks = filteredTasks.filter(t => t.status === column.id);
              return (
                <div 
                  key={column.id} 
                  className="w-80 flex flex-col bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)]"
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(column.id)}
                  data-testid={`column-${column.id}`}
                >
                  <div className="px-4 py-3 border-b border-[var(--surface-border)] flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${column.color}`} />
                    <span className="text-sm font-medium">{column.label}</span>
                    <Badge variant="outline" className="ml-auto text-xs">{columnTasks.length}</Badge>
                  </div>
                  <ScrollArea className="flex-1 p-3">
                    <div className="space-y-3">
                      {columnTasks.map(task => (
                        <div 
                          key={task.id} 
                          className="bg-[var(--surface-bg)] rounded-lg border border-[var(--surface-border)] p-3 cursor-grab active:cursor-grabbing hover:border-[var(--accent-indigo)]/30 transition-colors"
                          draggable
                          onDragStart={() => handleDragStart(task)}
                          data-testid={`task-card-${task.id}`}
                        >
                          <div className="flex items-start gap-2">
                            <GripVertical className="w-4 h-4 opacity-20 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{task.title}</p>
                              {task.description && <p className="text-xs opacity-50 line-clamp-2 mt-1">{task.description}</p>}
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                <Badge className={`text-xs h-5 ${priorityColors[task.priority]}`}>
                                  <Flag className="w-3 h-3 mr-1" /> {task.priority}
                                </Badge>
                                {task.task_type !== 'task' && (
                                  <Badge variant="outline" className="text-xs h-5">{task.task_type}</Badge>
                                )}
                                {task.epic_id && (
                                  <Badge variant="outline" className="text-xs h-5 bg-purple-500/10 text-purple-400 border-purple-500/20">
                                    {getEpicTitle(task.epic_id)}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center justify-between mt-3">
                                <div className="flex items-center gap-1.5 opacity-50">
                                  <User className="w-4 h-4" />
                                  <span className="text-xs">{getMemberName(task.assignee_id)}</span>
                                </div>
                                <Button variant="ghost" size="icon" className="h-6 w-6 opacity-30 hover:opacity-100 hover:text-red-400" onClick={() => handleDeleteTask(task.id)} data-testid={`delete-task-${task.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {columnTasks.length === 0 && (
                        <div className="py-10 text-center opacity-30 text-sm">Drop tasks here</div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <ScrollArea className="flex-1">
          <div className="p-4">
            <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--surface-border)] bg-[var(--surface-bg)]">
                    <th className="text-left px-4 py-3 font-medium opacity-50">Title</th>
                    <th className="text-left px-4 py-3 font-medium opacity-50 w-28">Status</th>
                    <th className="text-left px-4 py-3 font-medium opacity-50 w-24">Priority</th>
                    <th className="text-left px-4 py-3 font-medium opacity-50 w-32">Assignee</th>
                    <th className="text-left px-4 py-3 font-medium opacity-50 w-24">Type</th>
                    <th className="text-right px-4 py-3 font-medium opacity-50 w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map(task => (
                    <tr key={task.id} className="border-b border-[var(--surface-border)] last:border-0 hover:bg-[var(--surface-card)]" data-testid={`task-row-${task.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{task.title}</span>
                          {task.epic_id && <Badge variant="outline" className="text-xs h-5 bg-purple-500/10 text-purple-400 border-purple-500/20">{getEpicTitle(task.epic_id)}</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={task.status} onValueChange={(v) => handleUpdateTask(task.id, { status: v })}>
                          <SelectTrigger className="h-8 text-sm bg-transparent border-[var(--surface-border)]"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                            {statusColumns.map(s => <SelectItem key={s.id} value={s.id} className="text-sm">{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`text-xs ${priorityColors[task.priority]}`}>{task.priority}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span className="opacity-60">{getMemberName(task.assignee_id)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">{task.task_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-40 hover:opacity-100 hover:text-red-400" onClick={() => handleDeleteTask(task.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filteredTasks.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-12 text-center opacity-30">No tasks found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
